import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const CATEGORY_PRODUCT_TYPES = {
    "Home & Kitchen": ["Kitchen Appliances", "Home Decor", "Cookware", "Storage Solutions", "Cleaning Supplies", "Furniture", "Lighting", "Bedding", "Bath Accessories"],
    "Beauty & Personal Care": ["Skincare", "Makeup", "Hair Care", "Fragrances", "Personal Hygiene", "Oral Care", "Body Care", "Beauty Tools", "Nail Care"],
    "Clothing, Shoes & Jewelry": ["Women's Clothing", "Men's Clothing", "Shoes", "Jewelry", "Watches", "Handbags", "Accessories", "Athletic Wear", "Formal Wear"],
    "Electronics": ["Smartphones", "Laptops", "Headphones", "Tablets", "Smartwatches", "Gaming Accessories", "Cameras", "Audio Equipment", "Computer Accessories"],
    "Health & Household": ["Vitamins & Supplements", "Medical Supplies", "Household Essentials", "Baby Care", "Wellness Products", "First Aid", "Health Monitors", "Mobility Aids"],
    "Toys & Games": ["Educational Toys", "Action Figures", "Board Games", "Building Blocks", "Dolls", "Electronic Toys", "Outdoor Play", "Puzzles", "Arts & Crafts"],
    "Sports & Outdoors": ["Fitness Equipment", "Outdoor Gear", "Sports Apparel", "Camping Equipment", "Exercise Accessories", "Team Sports", "Water Sports", "Winter Sports"],
    "Baby": ["Baby Feeding", "Diapers", "Baby Clothing", "Strollers", "Car Seats", "Baby Monitors", "Baby Toys", "Nursery Furniture", "Baby Safety"],
    "Pet Supplies": ["Dog Supplies", "Cat Supplies", "Pet Food", "Pet Toys", "Pet Health", "Pet Grooming", "Aquarium Supplies", "Bird Supplies", "Small Animal Care"],
    "Automotive Parts & Accessories": ["Car Electronics", "Interior Accessories", "Exterior Accessories", "Tools & Equipment", "Oils & Fluids", "Replacement Parts", "Tires & Wheels"],
    "Appliances": ["Kitchen Appliances", "Laundry Appliances", "Small Appliances", "Vacuum Cleaners", "Air Conditioners", "Heating Systems", "Water Heaters"],
    "Arts, Crafts & Sewing": ["Drawing & Painting", "Sewing Supplies", "Craft Kits", "Scrapbooking", "Jewelry Making", "Fabric", "Art Supplies", "Craft Tools"],
    "Books": ["Fiction", "Non-Fiction", "Educational", "Children's Books", "Self-Help", "Biographies", "Textbooks", "Reference Books", "E-books"],
    "CDs & Vinyl": ["Pop Music", "Rock Music", "Classical", "Jazz", "Country", "Hip-Hop", "Electronic", "Vinyl Records", "Music Accessories"],
    "Cell Phones & Accessories": ["Smartphones", "Phone Cases", "Chargers", "Screen Protectors", "Phone Holders", "Bluetooth Accessories", "Phone Parts"],
    "Collectibles & Fine Art": ["Collectible Toys", "Antiques", "Coins", "Stamps", "Trading Cards", "Fine Art", "Memorabilia", "Vintage Items"],
    "Computers": ["Laptops", "Desktop Computers", "Computer Parts", "Monitors", "Keyboards & Mice", "Software", "Networking Equipment", "Storage Devices"],
    "Garden & Outdoor": ["Garden Tools", "Plants & Seeds", "Outdoor Furniture", "Grills & Outdoor Cooking", "Lawn Care", "Garden Decor", "Outdoor Lighting"],
    "Grocery & Gourmet Food": ["Snacks", "Beverages", "Gourmet Foods", "Organic Products", "International Foods", "Health Foods", "Cooking Ingredients"],
    "Handmade": ["Handmade Jewelry", "Handmade Home Decor", "Handmade Clothing", "Artisan Crafts", "Custom Items", "Handmade Toys", "Personalized Gifts"],
    "Industrial & Scientific": ["Lab Equipment", "Industrial Tools", "Safety Equipment", "Measuring Instruments", "Industrial Supplies", "Scientific Instruments"],
    "Kitchen & Dining": ["Cookware", "Kitchen Appliances", "Dinnerware", "Kitchen Tools", "Food Storage", "Kitchen Linens", "Barware", "Coffee & Tea"],
    "Movies & TV": ["DVDs", "Blu-ray", "Digital Movies", "TV Shows", "Documentary Films", "Foreign Films", "Classic Movies", "Movie Merchandise"],
    "Musical Instruments": ["Guitars", "Keyboards", "Drums", "Wind Instruments", "String Instruments", "Audio Equipment", "Music Accessories", "Sheet Music"],
    "Office Products": ["Office Supplies", "Office Furniture", "Office Electronics", "Filing & Organization", "Writing & Correction", "Presentation Supplies"],
    "Patio, Lawn & Garden": ["Patio Furniture", "Garden Tools", "Lawn Mowers", "Outdoor Decor", "Plant Care", "Watering Equipment", "Outdoor Storage"],
    "Software": ["Operating Systems", "Business Software", "Security Software", "Design Software", "Gaming Software", "Educational Software", "Utilities"],
    "Tools & Home Improvement": ["Power Tools", "Hand Tools", "Hardware", "Electrical", "Plumbing", "Paint & Supplies", "Storage & Organization", "Safety Equipment"],
    "Video Games": ["Gaming Consoles", "Video Game Software", "Gaming Accessories", "Gaming Headsets", "Controllers", "Gaming Chairs", "Gaming Monitors"]
};

const PLAN_INFO = {
    starter:  { label: 'Starter (Free)',  monthly: 'Free',      yearly: 'Free',       icon: 'fa-seedling' },
    pro:      { label: 'Pro',             monthly: '$19/mo',    yearly: '$190/yr',    icon: 'fa-bolt' },
    premium:  { label: 'Premium',         monthly: '$39/mo',    yearly: '$390/yr',    icon: 'fa-star' },
    elite:    { label: 'Elite',           monthly: '$79/mo',    yearly: '$790/yr',    icon: 'fa-crown' },
};

const CATEGORIES = Object.keys(CATEGORY_PRODUCT_TYPES);

export default function CheckoutOptions() {
    const { authFetch } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    const params = new URLSearchParams(location.search);
    const plan    = params.get('plan')    || 'pro';
    const billing = params.get('billing') || 'monthly';

    const [category, setCategory]       = useState('');
    const [productType, setProductType] = useState('');
    const [minPrice, setMinPrice]       = useState('');
    const [maxPrice, setMaxPrice]       = useState('');
    const [minRoi, setMinRoi]           = useState('10');
    const [loading, setLoading]         = useState(false);
    const [error, setError]             = useState('');

    const ROI_OPTIONS = Array.from({ length: 10 }, (_, i) => (i + 1) * 10);

    const productTypes = category ? CATEGORY_PRODUCT_TYPES[category] || [] : [];

    useEffect(() => { setProductType(''); }, [category]);

    const planInfo = PLAN_INFO[plan] || PLAN_INFO.pro;

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!category || !productType) {
            setError('Please select both a category and a product type.');
            return;
        }
        setLoading(true);
        setError('');
        if (minPrice && maxPrice && Number(maxPrice) <= Number(minPrice)) {
            setError('Max price must be greater than min price.');
            setLoading(false);
            return;
        }
        try {
            const body = { plan, billing, category, product_type: productType, min_roi: minRoi };
            if (minPrice !== '') body.min_price = minPrice;
            if (maxPrice !== '') body.max_price = maxPrice;
            const res = await authFetch('/api/stripe/create-checkout', {
                method: 'POST',
                body: JSON.stringify(body)
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || 'Could not start checkout');
            window.location.href = data.url;
        } catch (err) {
            setError(err.message);
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4">
            <div className="w-full max-w-lg">

                {/* Header */}
                <div className="text-center mb-8">
                    <Link to="/" className="font-extrabold text-3xl" style={{ color: '#1a1a2e' }}>
                        Seller <span style={{ color: '#e94560' }}>Source</span>
                    </Link>
                    <h2 className="mt-4 text-2xl font-bold text-gray-900">Customise Your Report</h2>
                    <p className="text-gray-500 text-sm mt-1">Choose the category you want to find arbitrage opportunities in</p>
                </div>

                {/* Selected plan badge */}
                <div className="flex items-center gap-3 px-4 py-3 rounded-xl mb-6 text-sm font-semibold"
                    style={{ background: 'rgba(233,69,96,0.08)', border: '1.5px solid rgba(233,69,96,0.25)', color: '#c73652' }}>
                    <i className={`fas ${planInfo.icon} text-base flex-shrink-0`}></i>
                    <div>
                        <span className="font-bold">{planInfo.label}</span>
                        <span className="text-gray-400 font-normal ml-2">— {planInfo[billing] || planInfo.monthly}</span>
                        <span className="text-gray-400 font-normal ml-1 text-xs capitalize">({billing})</span>
                    </div>
                    <Link to="/pricing" className="ml-auto text-xs underline text-gray-400 hover:text-gray-600 font-normal">Change</Link>
                </div>

                {/* Form card */}
                <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">

                    {/* What gets analysed */}
                    <div className="mb-6 p-4 rounded-xl" style={{ background: '#f0f2f8' }}>
                        <h4 className="text-sm font-bold text-gray-700 mb-2">
                            <i className="fas fa-robot mr-2" style={{ color: '#e94560' }}></i>What our AI analyses for you
                        </h4>
                        <ul className="text-xs text-gray-500 space-y-1">
                            <li><i className="fas fa-check text-green-500 mr-2"></i>Scans top 100,000 Amazon BSR rankings</li>
                            <li><i className="fas fa-check text-green-500 mr-2"></i>Finds products with 7 or fewer units in stock</li>
                            <li><i className="fas fa-check text-green-500 mr-2"></i>Delivers exactly 4 premium opportunities per report</li>
                            <li><i className="fas fa-check text-green-500 mr-2"></i>Calculates ROI automatically — guarantees non-zero profits</li>
                        </ul>
                    </div>

                    {error && (
                        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700 text-sm">
                            <i className="fas fa-exclamation-circle flex-shrink-0"></i>
                            <span>{error}</span>
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-5">
                        {/* Category */}
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                                Category <span style={{ color: '#e94560' }}>*</span>
                            </label>
                            <div className="relative">
                                <select
                                    value={category}
                                    onChange={e => setCategory(e.target.value)}
                                    required
                                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none text-sm appearance-none bg-white pr-10"
                                    style={{ borderColor: category ? '#e94560' : undefined }}>
                                    <option value="">Select Category</option>
                                    {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                                </select>
                                <i className="fas fa-chevron-down absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs pointer-events-none"></i>
                            </div>
                        </div>

                        {/* Product Type */}
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                                Product Type <span style={{ color: '#e94560' }}>*</span>
                            </label>
                            <div className="relative">
                                <select
                                    value={productType}
                                    onChange={e => setProductType(e.target.value)}
                                    required
                                    disabled={!category}
                                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none text-sm appearance-none bg-white pr-10 disabled:bg-gray-50 disabled:text-gray-400"
                                    style={{ borderColor: productType ? '#e94560' : undefined }}>
                                    <option value="">{category ? 'Select Product Type' : 'Select a category first'}</option>
                                    {productTypes.map(pt => <option key={pt} value={pt}>{pt}</option>)}
                                </select>
                                <i className="fas fa-chevron-down absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs pointer-events-none"></i>
                            </div>
                            {category && (
                                <p className="text-xs text-gray-400 mt-1">Specific product type within {category}</p>
                            )}
                        </div>

                        {/* Price Range */}
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                                    Min Price ($)
                                </label>
                                <div className="relative">
                                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm font-bold pointer-events-none">$</span>
                                    <input
                                        type="number"
                                        min="0"
                                        step="1"
                                        placeholder="e.g. 10"
                                        value={minPrice}
                                        onChange={e => setMinPrice(e.target.value)}
                                        className="w-full pl-7 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none text-sm bg-white"
                                        style={{ borderColor: minPrice ? '#e94560' : undefined }}
                                    />
                                </div>
                                <p className="text-xs text-gray-400 mt-1">Lowest buy price</p>
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                                    Max Price ($)
                                </label>
                                <div className="relative">
                                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm font-bold pointer-events-none">$</span>
                                    <input
                                        type="number"
                                        min="0"
                                        step="1"
                                        placeholder="e.g. 150"
                                        value={maxPrice}
                                        onChange={e => setMaxPrice(e.target.value)}
                                        className="w-full pl-7 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none text-sm bg-white"
                                        style={{ borderColor: maxPrice ? '#e94560' : undefined }}
                                    />
                                </div>
                                <p className="text-xs text-gray-400 mt-1">Highest buy price</p>
                            </div>
                        </div>

                        {/* Minimum ROI */}
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                                Minimum ROI <span style={{ color: '#e94560' }}>*</span>
                            </label>
                            <div className="relative">
                                <select
                                    value={minRoi}
                                    onChange={e => setMinRoi(e.target.value)}
                                    required
                                    className="w-full px-4 py-3 border-2 rounded-xl focus:outline-none text-sm appearance-none bg-white pr-10"
                                    style={{ borderColor: '#e94560' }}>
                                    {ROI_OPTIONS.map(roi => (
                                        <option key={roi} value={String(roi)}>{roi}%</option>
                                    ))}
                                </select>
                                <i className="fas fa-chevron-down absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs pointer-events-none"></i>
                            </div>
                            <p className="text-xs text-gray-400 mt-1">Only show opportunities with at least this ROI</p>
                        </div>

                        <button
                            type="submit"
                            disabled={loading || !category || !productType}
                            className="w-full py-4 rounded-xl font-bold text-white text-sm transition-all hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                            style={{ background: '#e94560' }}>
                            {loading ? (
                                <>
                                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                                    Preparing Checkout...
                                </>
                            ) : (
                                <>
                                    <i className="fas fa-credit-card"></i>
                                    Proceed to Payment — {planInfo.price}
                                </>
                            )}
                        </button>
                    </form>
                </div>

                <p className="text-center text-xs text-gray-400 mt-5">
                    <i className="fas fa-lock mr-1"></i>
                    Secure payment by Stripe · Your report is generated after payment
                </p>
            </div>
        </div>
    );
}
